/**
 * Polls `POST /api/focus` every ~2000ms with the latest `TelemetryWindow`
 * snapshot and holds the last `FocusJudgment`. `fetchImpl`/`baseUrl`/`now`
 * are injectable so `test/client/focus.test.ts` can drive this against a
 * fake fetch (and a fake clock) instead of a real server.
 *
 * On a network error the last judgment is kept as-is rather than cleared —
 * `isFocused`'s own `maxAgeMs` (see `src/focus/gate.ts`) is what makes an
 * old judgment stop counting as "focused", so there's no separate "stale"
 * flag to track here.
 *
 * CLOCK: the server's `t` on the `/api/focus` response is `Date.now()`
 * (wall-clock epoch ms) but `isFocused` is always called with the game
 * loop's `performance.now()`-derived `nowMs` (see `client/game.ts`'s RAF
 * `frame`) — those two clocks have unrelated origins, so comparing them
 * directly makes `now - j.t` wildly (and often negatively) wrong and a
 * judgment never ages out. `#judgment.t` is stamped with `#now()`
 * (defaults to `performance.now`) at the moment the response is *received*
 * on the client instead, matching the clock `isFocused`'s caller uses; the
 * server's `t` is intentionally discarded.
 */

import type { FocusJudgment, FocusTelemetry } from "../../src";

const POLL_INTERVAL_MS = 2000;

export const TYPESAFE_UNCONFIGURED_BANNER =
  "TypeSafe not configured — focus judge offline, jump scare disabled";

export interface FocusClientOptions {
  fetchImpl?: typeof fetch;
  baseUrl?: string;
  /** Clock used to stamp a received judgment's `t`; defaults to `performance.now`. Injectable for tests. */
  now?: () => number;
}

export class FocusClient {
  readonly #fetchImpl: typeof fetch;
  readonly #baseUrl: string;
  readonly #now: () => number;
  #lastAttemptMs = -Infinity;
  #inFlight = false;
  #healthChecked = false;
  #judgment: FocusJudgment | null = null;
  #banner: string | null = null;

  constructor(opts: FocusClientOptions = {}) {
    this.#fetchImpl = opts.fetchImpl ?? fetch;
    this.#baseUrl = opts.baseUrl ?? "";
    this.#now = opts.now ?? (() => performance.now());
  }

  getJudgment(): FocusJudgment | null {
    return this.#judgment;
  }

  getBanner(): string | null {
    return this.#banner;
  }

  /** Fire-and-forget, once: seeds the unconfigured banner from /api/health before the first /api/focus round-trip lands. */
  checkHealth(): void {
    if (this.#healthChecked) return;
    this.#healthChecked = true;
    void this.#runHealthCheck();
  }

  async #runHealthCheck(): Promise<void> {
    try {
      const res = await this.#fetchImpl(`${this.#baseUrl}/api/health`);
      if (!res.ok) return;
      const body = (await res.json()) as { typesafeConfigured?: boolean };
      if (body.typesafeConfigured === false) {
        this.#banner = TYPESAFE_UNCONFIGURED_BANNER;
      }
    } catch {
      // No health signal yet — leave the banner as it already was.
    }
  }

  /** Call every frame; internally throttles to at most one in-flight POST per POLL_INTERVAL_MS. */
  poll(nowMs: number, telemetry: FocusTelemetry): void {
    if (this.#inFlight) return;
    if (nowMs - this.#lastAttemptMs < POLL_INTERVAL_MS) return;
    this.#lastAttemptMs = nowMs;
    this.#inFlight = true;
    void this.#send(telemetry).finally(() => {
      this.#inFlight = false;
    });
  }

  async #send(telemetry: FocusTelemetry): Promise<void> {
    try {
      const res = await this.#fetchImpl(`${this.#baseUrl}/api/focus`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(telemetry),
      });
      if (res.status === 503) {
        this.#banner = TYPESAFE_UNCONFIGURED_BANNER;
        return;
      }
      if (!res.ok) {
        return; // 400/429/502 — keep the last judgment, try again next poll.
      }
      const body = (await res.json()) as { p: number; t: number };
      // `body.t` is the server's own clock (epoch ms) — not used here; see
      // the module doc comment. Stamp receipt time on the client's clock so
      // it's comparable to the `nowMs` the game loop later passes to
      // `isFocused`.
      this.#judgment = { p: body.p, t: this.#now(), source: "typesafe" };
      this.#banner = null;
    } catch {
      // Network error — keep the last judgment; isFocused()'s maxAgeMs ages it out.
    }
  }
}
